import type React from "react";

import { Loader, Lock, User, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/apple-touch-icon.png";
import { useApi } from "../components/api/loginapi";

export default function Auth() {
  const [isLoginPage, setIsLoginPage] = useState<boolean>(true);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const api = useApi();
  const isLoading = api.isLoading;
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoginPage(true);
  }, []);

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const result = await api.post("/login", {
      username: username.trim(),
      password: password.trim(),
    });

    if (result.error) {
      setError(result.error);
    } else {
      console.log(result);

      localStorage.setItem("token", result.token);
      localStorage.setItem("role", result.role);

      if (result.role === "admin") {
        navigate("/vas", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          {/* Improved logo placement */}
          <div className="flex justify-center mb-4">
            <div className="h-24 w-24 relative">
              <img
                src={Logo}
                alt="Zenith Logo"
                className="object-contain shadow-sm rounded-lg"
              />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-red-600">
            Zenith <span className="text-black">Report Portal</span>
          </h2>
          <p className="mt-2 text-sm text-gray-700 font-medium">
            Please enter your credentials to continue
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-md rounded-lg border border-gray-100">
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 text-sm border rounded-md bg-red-50 text-red-600 border-red-200">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-800"
              >
                Username
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  placeholder="Username"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-800"
              >
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  placeholder="Password"
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 disabled:opacity-50 transition-colors shadow-sm"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Logging in...
                  </span>
                ) : (
                  "Login"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
